<template>
  <vxe-modal v-model="visible" :loading="loading" :title="$t('business.label.dictionaryInfo')" type="confirm" width="50%" @confirm="confirm">
    <a-form-model ref="form" :colon="false" :label-col="labelCol" :model="dictionary" :rules="rules" :wrapper-col="wrapperCol">
      <a-row style="margin-left: -4%;margin-right: 4%">
        <a-col span="12">
          <a-form-model-item :label="$t('business.label.no')" prop="no">
            <vxe-input v-model="dictionary.no" style="width: 100%"/>
          </a-form-model-item>
        </a-col>
        <a-col span="12">
          <a-form-model-item :label="$t('business.label.name')" prop="name">
            <vxe-input v-model="dictionary.name" style="width: 100%"/>
          </a-form-model-item>
        </a-col>
        <a-col span="12">
          <a-form-model-item :label="$t('business.label.ordinal')" prop="ordinal">
            <vxe-number-input v-model="dictionary.ordinal" :min="0" style="width: 100%"/>
          </a-form-model-item>
        </a-col>
        <a-col span="24">
          <a-form-model-item :label="$t('business.label.dictionaryValue')" :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
            <template v-if="isJSONObject">
              <vxe-card>
                <a-row style="margin-left: -2%;margin-right: 2%">
                  <a-col v-for="item in valueItems" v-if="valueObject && valueObject[item.field]!==undefined" :key="item.field" span="12">
                    <a-form-model-item :label="$t(item.title)" :label-col="{ span: 8 }" :name="'valueObject.' + item.field" :wrapper-col="{ span: 16 }">
                      <component :is="item.type" v-model:value="valueObject[item.field]" style="width: 100%" v-bind="item.props"/>
                    </a-form-model-item>
                  </a-col>
                </a-row>
              </vxe-card>
            </template>
            <template v-else>
              <vxe-input v-model="dictionary.value" style="width: 100%"/>
            </template>
          </a-form-model-item>
        </a-col>
        <a-col span="24">
          <a-form-model-item :label="$t('business.label.enterprise')" :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
            <vxe-select v-model="enterpriseIds" :options="enterprises" multiple style="width: 100%"/>
          </a-form-model-item>
        </a-col>
        <a-col span="24">
          <a-form-model-item :label="$t('business.label.warehouse')" :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
            <vxe-select v-model="warehouseIds" :options="warehouses" multiple style="width: 100%"/>
          </a-form-model-item>
        </a-col>
      </a-row>
    </a-form-model>
  </vxe-modal>
</template>

<script>
import { findAll, save } from '@/api/Api'
import { Field } from '@/api/dto/admin/Field'
import { Dictionary } from '@/api/dto/admin/Dictionary'
import { getDictionaries, getEnums, getOptions } from '@/utils/util'

export default {
  name: 'DictionaryEditor',
  data() {
    return {
      visible: false,
      loading: false,
      field: new Field(),
      dictionary: new Dictionary(),
      valueObject: {},
      enterpriseIds: [],
      warehouseIds: [],
      enterprises: [],
      warehouses: [],
      logicalStores: [],
      distributionTypes: [],
      storageRoomTypes: [],
      inventoryStates: [],
      stagingTypes: [],
      pileupTypes: [],
      matrixSortingModes: [],
      labelCol: { span: 8 },
      wrapperCol: { span: 16 },
      rules: {
        no: [{ required: true, message: this.$t('message.canNotBeEmpty') }],
        name: [{ required: true, message: this.$t('message.canNotBeEmpty') }],
        ordinal: [{ required: true, type: 'number', min: 0, message: this.$t('message.canNotBeEmpty') }]
      },
      valueConfig: {
        CONFIG_NAVIGATION: { step: 0, icon: null, url: null, verifyType: null },
        CONVEYOR: { workload: 0 },
        CUSTOMER_CLASSIFICATION: { scattered: false },
        ITEM_TYPE: { releasable: false, modelControl: false },
        ITEM_CATEGORY3: { daysOfExpirationNearly: 360 },
        LEGAL_ATTR: { approvalConfirm: false, autoAccepting: false, duplex: false, licenseCheck: false, instruments: false, patentMedicine: false, chineseMedicine: false },
        LOCATION_REGION: { columnMonopoly: false, reservedLocations: 0 },
        LOCK_TYPE: { group: null, intercept: false, interceptionType: null },
        LOGICAL_STORE: { mergeOrder: false, parentId: null, disqualifiedId: null },
        ORDER_PROPERTY: { category: 0 },
        ORDER_STRATEGY: { computer: null, threshold: 0 },
        PACKING_MODE: { mechanical: false, numberOfPickingOrder: 0 },
        PICKUP_MODE: { distributionType: 0, mergeOrder: false, autoIssue: false, autoStaging: false, ordinal: 0 },
        RECEIPT_CONCLUSION: { storageRoomType: null, inventoryState: null, logicalStoreId: null },
        REGION: { supervised: false, print: false },
        RETURN_CONCLUSION: { storageRoomType: null, inventoryState: null, logicalStoreId: null },
        SEEDING_MODE: { mechanical: false },
        STAGING_TYPE: { physical: false, alternateTypeId: null, pileupType: null, matrixSortingMode: null },
        WAVE_STRATEGY: { arrangeFinishTime: '00:00', arrangeStartTime: '00:00', autoIssue: false, autoReplenish: false, issueFinishTime: '00:00', issueStartTime: '00:00', progress: 0, quantity: 0 },
        ZONE_REGION: { mergeOrder: false }
      }
    }
  },
  created() {
    findAll('enterpriseFactory', { available: true }).then(res => {
      if (res.success) {
        this.enterprises = getOptions(res.result)
      }
      return findAll('warehouseFactory', { all: true })
    }).then(res => {
      if (res.success) {
        this.warehouses = getOptions(res.result)
      }
      return getDictionaries('LOGICAL_STORE')
    }).then(result => {
      this.logicalStores = result
      return getEnums('DistributionType')
    }).then(result => {
      this.distributionTypes = result
      return getEnums('StorageRoomType')
    }).then(result => {
      this.storageRoomTypes = result
      return getEnums('InventoryState')
    }).then(result => {
      this.inventoryStates = result
      return getDictionaries('STAGING_TYPE')
    }).then(result => {
      this.stagingTypes = result
      return getEnums('PileupType')
    }).then(result => {
      this.pileupTypes = result
      return getEnums('MatrixSortingMode')
    }).then(result => {
      this.matrixSortingModes = result
    })
  },
  computed: {
    valueItems() {
      return [
        {
          type: 'VxeDatePicker',
          field: 'arrangeStartTime',
          title: 'business.label.arrangeStartTime',
          props: {
            type: 'time',
            clearable: true,
            valueFormat: 'HH:mm',
            labelFormat: 'HH:mm'
          }
        },
        {
          type: 'VxeDatePicker',
          field: 'arrangeFinishTime',
          title: 'business.label.arrangeFinishTime',
          props: {
            type: 'time',
            clearable: true,
            valueFormat: 'HH:mm',
            labelFormat: 'HH:mm'
          }
        },
        {
          type: 'VxeDatePicker',
          field: 'issueStartTime',
          title: 'business.label.issueStartTime',
          props: {
            type: 'time',
            clearable: true,
            valueFormat: 'HH:mm',
            labelFormat: 'HH:mm'
          }
        },
        {
          type: 'VxeDatePicker',
          field: 'issueFinishTime',
          title: 'business.label.issueFinishTime',
          props: {
            type: 'time',
            clearable: true,
            valueFormat: 'HH:mm',
            labelFormat: 'HH:mm'
          }
        },
        {
          type: 'VxeInput',
          field: 'icon',
          title: 'business.label.icon'
        },
        {
          type: 'VxeInput',
          field: 'url',
          title: 'business.label.url'
        },
        {
          type: 'VxeInput',
          field: 'verifyType',
          title: 'business.label.verifyType'
        },
        {
          type: 'VxeInput',
          field: 'group',
          title: 'business.label.group'
        },
        {
          type: 'VxeInput',
          field: 'interceptionType',
          title: 'business.label.interceptionType'
        },
        {
          type: 'VxeInput',
          field: 'computer',
          title: 'business.label.computer'
        },
        {
          type: 'VxeNumberInput',
          field: 'step',
          title: 'business.label.step'
        },
        {
          type: 'VxeNumberInput',
          field: 'workload',
          title: 'business.label.workload'
        },
        {
          type: 'VxeNumberInput',
          field: 'daysOfExpirationNearly',
          title: 'business.label.daysOfExpirationNearly'
        },
        {
          type: 'VxeNumberInput',
          field: 'reservedLocations',
          title: 'business.label.reservedLocations'
        },
        {
          type: 'VxeNumberInput',
          field: 'category',
          title: 'business.label.category'
        },
        {
          type: 'VxeNumberInput',
          field: 'threshold',
          title: 'business.label.threshold'
        },
        {
          type: 'VxeNumberInput',
          field: 'numberOfPickingOrder',
          title: 'business.label.numberOfPickingOrder'
        },
        {
          type: 'VxeNumberInput',
          field: 'ordinal',
          title: 'business.label.ordinal'
        },
        {
          type: 'VxeNumberInput',
          field: 'quantity',
          title: 'business.label.quantity'
        },
        {
          type: 'VxeNumberInput',
          field: 'progress',
          title: 'business.label.progress',
          props: {
            step: 0.01
          }
        },
        {
          type: 'VxeSelect',
          field: 'parentId',
          title: 'business.label.parent',
          props: {
            options: this.logicalStores
          }
        },
        {
          type: 'VxeSelect',
          field: 'disqualifiedId',
          title: 'business.label.disqualified',
          props: {
            options: this.logicalStores
          }
        },
        {
          type: 'VxeSelect',
          field: 'distributionType',
          title: 'business.label.distributionType',
          props: {
            options: this.distributionTypes
          }
        },
        {
          type: 'VxeSelect',
          field: 'storageRoomType',
          title: 'business.label.storageRoomType',
          props: {
            options: this.storageRoomTypes
          }
        },
        {
          type: 'VxeSelect',
          field: 'inventoryState',
          title: 'business.label.inventoryState',
          props: {
            options: this.inventoryStates
          }
        },
        {
          type: 'VxeSelect',
          field: 'logicalStoreId',
          title: 'business.label.logicalStore',
          props: {
            options: this.logicalStores
          }
        },
        {
          type: 'VxeSelect',
          field: 'alternateTypeId',
          title: 'business.label.alternateType',
          props: {
            options: this.stagingTypes
          }
        },
        {
          type: 'VxeSelect',
          field: 'pileupType',
          title: 'business.label.pileupType',
          props: {
            options: this.pileupTypes
          }
        },
        {
          type: 'VxeSelect',
          field: 'matrixSortingMode',
          title: 'business.label.matrixSortingMode',
          props: {
            options: this.matrixSortingModes
          }
        },
        {
          type: 'VxeSwitch',
          field: 'scattered',
          title: 'business.label.scattered'
        },
        {
          type: 'VxeSwitch',
          field: 'releasable',
          title: 'business.label.releasable'
        },
        {
          type: 'VxeSwitch',
          field: 'modelControl',
          title: 'business.label.modelControl'
        },
        {
          type: 'VxeSwitch',
          field: 'approvalConfirm',
          title: 'business.label.approvalConfirm'
        },
        {
          type: 'VxeSwitch',
          field: 'autoAccepting',
          title: 'business.label.autoAccepting'
        },
        {
          type: 'VxeSwitch',
          field: 'duplex',
          title: 'business.label.duplex'
        },
        {
          type: 'VxeSwitch',
          field: 'licenseCheck',
          title: 'business.label.licenseCheck'
        },
        {
          type: 'VxeSwitch',
          field: 'instruments',
          title: 'business.label.instruments'
        },
        {
          type: 'VxeSwitch',
          field: 'patentMedicine',
          title: 'business.label.patentMedicine'
        },
        {
          type: 'VxeSwitch',
          field: 'chineseMedicine',
          title: 'business.label.chineseMedicine'
        },
        {
          type: 'VxeSwitch',
          field: 'columnMonopoly',
          title: 'business.label.columnMonopoly'
        },
        {
          type: 'VxeSwitch',
          field: 'intercept',
          title: 'business.label.intercept'
        },
        {
          type: 'VxeSwitch',
          field: 'mergeOrder',
          title: 'business.label.mergeOrder'
        },
        {
          type: 'VxeSwitch',
          field: 'mechanical',
          title: 'business.label.automatic'
        },
        {
          type: 'VxeSwitch',
          field: 'autoIssue',
          title: 'business.label.autoIssue'
        },
        {
          type: 'VxeSwitch',
          field: 'autoStaging',
          title: 'business.label.autoStaging'
        },
        {
          type: 'VxeSwitch',
          field: 'supervised',
          title: 'business.label.supervised'
        },
        {
          type: 'VxeSwitch',
          field: 'print',
          title: 'business.label.print'
        },
        {
          type: 'VxeSwitch',
          field: 'physical',
          title: 'business.label.physical'
        },
        {
          type: 'VxeSwitch',
          field: 'autoReplenish',
          title: 'business.label.autoReplenish'
        }
      ]
    },
    isJSONObject() {
      if (typeof this.dictionary.value !== 'string') {
        return false
      }
      const trimmed = this.dictionary.value.trim()
      if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
        return false
      }
      try {
        const obj = JSON.parse(trimmed)
        return typeof obj === 'object' && obj !== null && !Array.isArray(obj)
      } catch {
        return false
      }
    }
  },
  watch: {
    'dictionary.value': {
      handler(newVal) {
        if (this.isJSONObject) {
          try {
            this.valueObject = JSON.parse(newVal.trim())
          } catch {
            this.valueObject = {}
          }
        } else {
          this.valueObject = {}
        }
      },
      immediate: true,
      deep: true
    },
    valueObject: {
      handler(newObj) {
        if (this.isJSONObject) {
          const serialized = JSON.stringify(newObj)
          if (serialized !== this.dictionary.value) {
            this.dictionary.value = serialized
          }
        }
      },
      deep: true
    }
  },
  methods: {
    add(field) {
      this.visible = true
      this.field = field
      this.dictionary = new Dictionary()
      this.dictionary.available = true
      this.enterpriseIds = []
      this.warehouseIds = []
    },
    edit(field, row, copy) {
      this.visible = true
      this.field = field
      if (copy) {
        row.id = undefined
        row.version = undefined
      }
      this.dictionary = new Dictionary(row)
      this.enterpriseIds = row.enterprises.map(item => item.id)
      this.warehouseIds = row.warehouses.map(item => item.id)
    },
    save() {
      this.loading = true
      const field = this.field.toJSONObject()
      const dictionary = this.dictionary.toJSONObject()
      dictionary.enterprises = this.enterpriseIds.map(item => {
        return { id: item }
      })
      dictionary.warehouses = this.warehouseIds.map(item => {
        return { id: item }
      })
      field.dictionaries = [dictionary]
      save('fieldPersister', field).then(res => {
        if (res.success) {
          this.$emit('success')
          this.$message.success(this.$t('message.saveSuccess'))
          this.visible = false
        }
      }).finally(() => this.loading = false)
    },
    confirm() {
      this.$refs.form.validate(valid => {
        if (valid) this.save()
      })
    }
  }
}
</script>